import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { allVideos } from '@/mocks/videos';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const video = allVideos.find((v) => v.slug === slug);

  const relatedVideos = useMemo(() => {
    if (!video) return [];
    return allVideos
      .filter((v) => v.id !== video.id && v.categorySlug === video.categorySlug)
      .slice(0, 4);
  }, [video]);

  const formatViews = (views: number): string => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  if (!video) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 pb-10 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">视频未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">抱歉，您访问的视频不存在或已下架</p>
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    headline: video.title,
    description: video.description,
    image: video.thumbnail,
    datePublished: video.publishDate,
    author: { '@type': 'Organization', name: '蘑菇视频在线观看' },
    publisher: { '@type': 'Organization', name: '蘑菇视频在线观看', logo: { '@type': 'ImageObject', url: 'https://moguvideo.cn/logo.png' } },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://moguvideo.cn/article/${slug}` },
  };

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://moguvideo.cn/' },
      { '@type': 'ListItem', position: 2, name: video.category, item: `https://moguvideo.cn/category/${video.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: video.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Article" data={articleSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <SchemaOrg type="VideoObject" data={{ name: video.title, description: video.description, thumbnailUrl: video.thumbnail, uploadDate: video.publishDate, duration: video.duration }} />
      <Navbar />
      <main className="min-h-screen pt-20 pb-10">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: video.category, href: `/category/${video.categorySlug}` },
              { label: video.title },
            ]}
          />

          <div className="relative aspect-video rounded-lg overflow-hidden bg-background-200 mb-6">
            <img
              src={video.thumbnail}
              alt={video.title}
              title={`蘑菇视频在线观看 - ${video.title}`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-white hover:bg-primary-600 transition-all duration-300 hover:scale-110 cursor-pointer">
                <i className="ri-play-fill text-4xl"></i>
              </div>
            </div>
            <div className="absolute bottom-3 right-3 bg-black/75 text-white text-sm px-2.5 py-1 rounded font-label">
              {video.duration}
            </div>
          </div>

          <header className="mb-6">
            <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 mb-3 leading-tight">
              {video.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-eye-line text-sm"></i>
                </div>
                {formatViews(video.views)} 次观看
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-sm text-secondary-500"></i>
                </div>
                {video.rating} 分
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line text-sm"></i>
                </div>
                {video.publishDate}
              </span>
              <Link
                to={`/category/${video.categorySlug}`}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors whitespace-nowrap"
              >
                {video.category}
              </Link>
            </div>
          </header>

          <div className="bg-background-100/70 rounded-lg p-5 md:p-6 mb-8">
            <h2 className="font-heading text-base font-semibold text-foreground-950 mb-3">视频简介</h2>
            <p className="text-sm text-foreground-600 leading-relaxed">{video.description}</p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {video.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-background-200/70 text-xs text-foreground-600"
                >
                  #{tag}
                </span>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
              <span>画质：{video.quality.join(' / ')}</span>
            </div>
          </div>

          {relatedVideos.length > 0 && (
            <section>
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop>
                {relatedVideos.map((v) => (
                  <VideoCard key={v.id} video={v} size="small" />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}